import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasVerified, setHasVerified] = useState(false);
  const [tokenData, setTokenData] = useState<{ email: string; type: string } | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  // Listen for auth state changes specifically for password recovery
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setVerifying(false);
        setHasVerified(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Extract and validate the password recovery token from URL
  useEffect(() => {
    if (authLoading) return;

    // If user is already logged in (e.g. via auto-login from recovery link), show the form
    if (user) {
      setVerifying(false);
      setHasVerified(true);
      return;
    }

    if (hasVerified) return;

    const verifyToken = async () => {
      try {
        // Try to get parameters from hash first (new Supabase format)
        const hashParams = new URLSearchParams(location.hash.substring(1));
        let token = hashParams.get("access_token");
        let type = hashParams.get("type");

        // If not in hash, try query params (alternative format)
        if (!token) {
          const queryParams = new URLSearchParams(location.search);
          token = queryParams.get("access_token");
          type = queryParams.get("type");
        }

        console.log("Reset password verification:", { hasToken: !!token, type });

        if (!token || type !== "recovery") {
          console.error("Missing or invalid reset token", { hasToken: !!token, type });
          setError("Invalid or expired password reset link. Please request a new one.");
          setVerifying(false);
          setHasVerified(true);
          return;
        }

        // Verify the token by attempting to use it with verifyOtp
        // This validates the token without establishing a full session
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token: token,
          type: 'recovery',
          email: "", // Email will be extracted from the token by Supabase
        });

        if (verifyError || !data.session) {
          console.error("Token verification failed:", verifyError);
          setError("Invalid or expired password reset link. Please request a new one.");
          setVerifying(false);
          setHasVerified(true);
          return;
        }

        // Store token data for use in password reset
        // Keep the access token for later use
        sessionStorage.setItem("resetAccessToken", data.session.access_token);
        sessionStorage.setItem("resetRefreshToken", data.session.refresh_token || "");
        
        // Important: DO NOT establish the session yet
        // We don't call setSession(), which keeps the user logged out
        // This prevents automatic redirect to home

        setTokenData({ 
          email: data.user?.email || "",
          type: "recovery" 
        });
        setVerifying(false);
        setHasVerified(true);

        // Clear the URL hash/query params to prevent re-verification
        window.history.replaceState(null, "", window.location.pathname);
      } catch (error) {
        console.error("Error verifying reset token:", error);
        setError("Invalid or expired password reset link. Please request a new one.");
        setVerifying(false);
        setHasVerified(true);
      }
    };

    verifyToken();
  }, [location.hash, location.search, hasVerified, user, authLoading]);

  const handleResetPassword = async (values: ResetPasswordFormValues) => {
    setLoading(true);
    try {
      // If user is NOT logged in, we need to restore the session from storage
      if (!user) {
        // Get the stored access token
        const accessToken = sessionStorage.getItem("resetAccessToken");
        const refreshToken = sessionStorage.getItem("resetRefreshToken");

        if (!accessToken) {
          throw new Error("Reset session expired. Please request a new password reset link.");
        }

        // Set the session with the stored token temporarily
        const { error: setSessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || "",
        });

        if (setSessionError) {
          console.error("Session setup error:", setSessionError);
          throw new Error("Failed to verify reset link. Please request a new one.");
        }
      }

      // Now that we have a valid session (either existing or restored), update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (updateError) {
        console.error("Password update error:", updateError);
        throw updateError;
      }

      // Clear the stored tokens
      sessionStorage.removeItem("resetAccessToken");
      sessionStorage.removeItem("resetRefreshToken");

      toast({
        title: "Password Reset Successful",
        description: "Your password has been reset. You will be signed out and can log in with your new password.",
      });

      // Clear the URL
      window.history.replaceState(null, "", window.location.pathname);
      
      // Sign out the user and redirect to login
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate("/auth");
      }, 1500);
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      
      // Clear tokens on error
      sessionStorage.removeItem("resetAccessToken");
      sessionStorage.removeItem("resetRefreshToken");
      
      toast({
        title: "Password Reset Failed",
        description: error instanceof Error ? error.message : "An unknown error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Verifying Reset Link</CardTitle>
            <CardDescription>Please wait while we verify your password reset link.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Reset Link Invalid</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={() => navigate("/auth")}
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input 
                id="password" 
                type="password" 
                {...form.register("password")} 
              />
              {form.formState.errors.password && (
                <p className="text-sm text-red-500">{form.formState.errors.password.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input 
                id="confirmPassword" 
                type="password" 
                {...form.register("confirmPassword")} 
              />
              {form.formState.errors.confirmPassword && (
                <p className="text-sm text-red-500">{form.formState.errors.confirmPassword.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {loading ? "Resetting Password..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
